import { Component, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { LoadingService } from 'src/app/services/loading.service';
import SignaturePad from 'signature_pad';
import { TrainingAnonymousService } from 'src/app/services/training-anonymous.service';
import { TrainingAnonymousModel } from 'src/app/models/training/training-anonymous.model';
import { TrainingConfirmationModel } from 'src/app/models/training-confirmation.model';
import { ConfirmationService } from 'src/app/services/confirmation.service';
import { ConfirmationResultModel } from 'src/app/models/confirmation-result.model';

@Component({
  selector: 'app-confirm-training-participation',
  templateUrl: './confirm-training-participation.component.html',
  styleUrl: './confirm-training-participation.component.scss',
  standalone: false
})
export class ConfirmTrainingParticipationComponent {
  @ViewChild('canvas') canvasRef!: ElementRef<HTMLCanvasElement>;
  signaturePad!: SignaturePad;

  trainingKey!: string;
  training!: TrainingAnonymousModel;

  form!: FormGroup;
  showPlaceholder = true;
  isSignatureEmpty = true;
  isSignatureValid = false;
  isFinished = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private loadingService: LoadingService,
    public translateService: TranslateService,
    private trainingAnonymousService: TrainingAnonymousService,
    private confirmationService: ConfirmationService,
  ) {
    
  }

  ngOnInit(): void {
    this.training = <TrainingAnonymousModel>{};
    this.trainingKey = this.route.snapshot.params['key'];

    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      accepted: [false, Validators.requiredTrue],
    });

    this.loadTrainingData();
  }

  ngAfterViewInit(): void {
    this.signaturePad = new SignaturePad(this.canvasRef.nativeElement, {
      minWidth: 1,
      maxWidth: 2,
      penColor: 'black',
    });

    this.signaturePad.addEventListener('beginStroke', () => {
      this.showPlaceholder = false;
      this.isSignatureValid = !this.signaturePad.isEmpty(); 
    });

    this.signaturePad.addEventListener('endStroke', () => {
      var isEmpty = this.signaturePad.isEmpty()
      this.showPlaceholder = isEmpty;
      this.isSignatureEmpty = isEmpty;
      this.isSignatureValid = !isEmpty; 
    });
  }

  get firstName() {
    return this.form.get('firstName')!;
  }

  get lastName() {
    return this.form.get('lastName')!;
  }

  get email() {
    return this.form.get('email')!;
  }

  loadTrainingData(accessToken: string | null = null): void {
    this.loadingService
      .load(
        this.trainingAnonymousService.getTrainingAnonymous(this.trainingKey, accessToken)
      )
      .subscribe((training) => {
        this.training = training;
      })
  }

  isConfirmed = false;
  isConfirmClicked = false;
  confirmationResultModel: ConfirmationResultModel = <ConfirmationResultModel>{};
  confirm(): void {
    this.isFinished = true;
    this.isConfirmClicked = true;

    if(!this.isSignatureValid) {
      return;
    }

    if (this.form.valid && !this.signaturePad.isEmpty()) {
      const trainingConfirmationModel: TrainingConfirmationModel = this.form.value;

      const signature = this.cropSignature(); // base64 image
      const signatureBase64Only = signature.split(',')[1];
      trainingConfirmationModel.eSignatureBase64 = signatureBase64Only;
      
      this.loadingService
        .load(
          this.confirmationService.confirmTrainingParticipation(
            this.trainingKey,
            trainingConfirmationModel,
          )
        )
        .subscribe({
          next: (confirmationResultModel) => {
            this.isConfirmed = true;
            this.confirmationResultModel = confirmationResultModel;
          },
          error: (error) => {
            console.error('Error confirming training participation:', error.error);
            this.isFinished = false;
          }
        });
    }
  }

  clearSignature(): void {
    this.signaturePad.clear();
    this.showPlaceholder = true;
    this.isSignatureEmpty = true;
    this.isSignatureValid = false;
  }

  cropSignature(): string {
    const padAsAny = this.signaturePad as any;
    const canvas: HTMLCanvasElement = padAsAny.canvas;

    const ctx = canvas.getContext('2d')!;
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const { data, width, height } = imageData;

    let top = height, bottom = 0, left = width, right = 0;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const index = (y * width + x) * 4;
        const alpha = data[index + 3]; // transparency

        if (alpha > 0) { // if transparency is more than zero, then there is a signature
          if (x < left) left = x;
          if (x > right) right = x;
          if (y < top) top = y;
          if (y > bottom) bottom = y;
        }
      }
    }

    if (right < left || bottom < top) 
      return canvas.toDataURL('image/png');

    const croppedWidth = right - left + 1;
    const croppedHeight = bottom - top + 1;

    const croppedCanvas = document.createElement('canvas');
    croppedCanvas.width = croppedWidth;
    croppedCanvas.height = croppedHeight;

    const croppedCtx = croppedCanvas.getContext('2d')!;
    croppedCtx.putImageData(
      ctx.getImageData(left, top, croppedWidth, croppedHeight),
      0, 0
    );

    return croppedCanvas.toDataURL('image/png');
  }
}
